import type {
  AutomationAreaDto,
  AutomationCaseStatus,
  AutomationCatalogDto,
  AutomationScenarioDto,
} from '@tms/shared';
import { Link } from '@tanstack/react-router';
import {
  Bolt,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Download,
  Folder,
  FolderOpen,
  FolderInput,
  HelpCircle,
  Link2,
  MinusCircle,
  Play,
  Search,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
} from '../../../components/ui';
import { useErrorText, useFormat, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { usePermissions } from '../../auth/permissions';
import { LinkToCaseDialog } from '../case-links/LinkToCaseDialog';
import { runReportHref, useAutomationCan } from '../hooks';
import { useCatalog, useMoveScenario, useRunArea } from './api';

/**
 * The autotest catalogue by product team: folders of teams and their areas on
 * the left, the chosen folder's scenarios on the right — by their readable
 * names, with how each went last. A repository mixing teams splits here; one
 * area's tests from several repositories come together.
 */

const SMOKE = 'smoke';
const UNSORTED = 'unsorted';

type Selection = { kind: 'team'; id: string } | { kind: 'area'; id: string };

interface Counts {
  scenarios: number;
  tests: number;
  red: number;
}

const countOf = (list: AutomationScenarioDto[]): Counts => ({
  scenarios: list.length,
  tests: list.reduce((sum, s) => sum + s.testCount, 0),
  red: list.filter((s) => s.last?.status === 'failed').length,
});

/** An area and every area under it. */
const descendants = (areas: AutomationAreaDto[], id: string): Set<string> => {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const area of areas) {
      if (area.parentId && out.has(area.parentId) && !out.has(area.id)) {
        out.add(area.id);
        grew = true;
      }
    }
  }
  return out;
};

const STATUS_STYLE = {
  passed: 'bg-pass-soft text-pass',
  failed: 'bg-fail-soft text-fail',
  skipped: 'bg-skip-soft text-skip',
  none: 'bg-sunken text-ink-muted',
} as const;

const StepIcon = ({ status }: { status: AutomationCaseStatus | undefined }) => {
  if (status === 'passed')
    return <CheckCircle2 className="size-3.5 shrink-0 text-pass" aria-hidden="true" />;
  if (status === 'failed' || status === 'broken')
    return <XCircle className="size-3.5 shrink-0 text-fail" aria-hidden="true" />;
  if (status === 'skipped')
    return <MinusCircle className="size-3.5 shrink-0 text-skip" aria-hidden="true" />;
  return <CircleDot className="size-3.5 shrink-0 text-ink-subtle" aria-hidden="true" />;
};

/* ─── the folder tree ─── */

const TreeRow = ({
  label,
  counts,
  depth,
  selected,
  open,
  icon,
  onSelect,
  title,
}: {
  label: string;
  counts: Counts;
  depth: number;
  selected: boolean;
  open?: boolean;
  icon?: 'bolt' | 'help';
  onSelect: () => void;
  title?: string;
}) => {
  const Icon =
    icon === 'bolt' ? Bolt : icon === 'help' ? HelpCircle : open || selected ? FolderOpen : Folder;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      title={title}
      className={cn(
        'flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left text-[13px] transition-colors',
        selected ? 'bg-accent-soft font-medium text-accent-fg' : 'text-ink hover:bg-sunken',
      )}
      style={{ paddingLeft: `${8 + depth * 16}px` }}
    >
      <Icon className="size-4 shrink-0 opacity-80" aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {counts.red > 0 ? (
        <span className="rounded bg-fail-soft px-1 text-[11px] font-medium text-fail">
          {counts.red}
        </span>
      ) : null}
      <span className="text-[11px] text-ink-subtle">{counts.scenarios}</span>
    </button>
  );
};

/* ─── one scenario ─── */

const ScenarioRow = ({
  scenario,
  areaPath,
  canManage,
  canLink,
  onMove,
  onLink,
}: {
  scenario: AutomationScenarioDto;
  /** shown in search results, where scenarios of many folders mix */
  areaPath: string | null;
  canManage: boolean;
  /** may link test cases (case:write) */
  canLink: boolean;
  onMove: (scenario: AutomationScenarioDto) => void;
  onLink: (scenario: AutomationScenarioDto) => void;
}) => {
  const { t } = useT();
  const { relative } = useFormat();
  const [open, setOpen] = useState(false);
  const last = scenario.last;
  const state = last ? last.status : 'none';
  const steps = scenario.steps.length
    ? scenario.steps
    : Object.keys(last?.steps ?? {}).map((name) => ({ name, method: null }));

  return (
    <li className="border-t border-border first:border-t-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-canvas"
      >
        {open ? (
          <ChevronDown className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
        ) : (
          <ChevronRight className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium text-ink">{scenario.title}</span>
            {scenario.segment ? (
              <Badge>
                {scenario.segment === 'IP'
                  ? t('automation.catalog.ip')
                  : t('automation.catalog.too')}
              </Badge>
            ) : null}
          </span>
          <span className="mt-0.5 block text-[12px] text-ink-muted">
            {last?.status === 'failed' && last.failedStep ? (
              <span className="text-fail">
                {t('automation.catalog.failedAt', { step: last.failedStep.name })}
                {last.failedStep.defect ? ` · ${last.failedStep.defect.id}` : ''}
              </span>
            ) : (
              t('automation.catalog.steps', { count: scenario.testCount })
            )}
            {scenario.caseKeys.length ? ` · ${scenario.caseKeys.join(', ')}` : ''}
            {` · ${scenario.repo ?? scenario.projectName}`}
            {areaPath ? ` · ${areaPath}` : ''}
          </span>
        </span>
        <span
          className={cn(
            'shrink-0 rounded-md px-1.5 py-0.5 text-[12px] font-medium',
            STATUS_STYLE[state],
          )}
        >
          {t(`automation.catalog.status.${state}`)}
        </span>
      </button>
      {open ? (
        <div className="space-y-3 px-4 pb-4 pl-11">
          {last?.failedStep?.message ? (
            <p className="whitespace-pre-wrap break-words rounded-md bg-fail-soft px-2 py-1.5 text-[12px] text-fail">
              {last.failedStep.message}
            </p>
          ) : null}
          {steps.length ? (
            <ol className="space-y-1">
              {steps.map((step) => (
                <li
                  key={`${step.method ?? ''}${step.name}`}
                  className="flex items-start gap-2 text-[13px]"
                >
                  <StepIcon status={last?.steps[step.name]} />
                  <span className="min-w-0 text-ink">{step.name}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-[12px] text-ink-subtle">{t('automation.catalog.noSteps')}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 text-[12px]">
            {last ? (
              <>
                <Link
                  to="/automation/runs/$runId"
                  params={{ runId: last.pipelineId }}
                  search={{ project: scenario.projectId }}
                  className="text-accent hover:underline"
                >
                  {t('automation.catalog.openRun', { id: last.pipelineId })}
                  {last.at ? ` · ${relative(last.at)}` : ''}
                </Link>
                <a
                  href={runReportHref(last.pipelineId, scenario.projectId)}
                  download
                  title={t('automation.runs.downloadReportHint')}
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  <Download className="size-3.5" aria-hidden="true" />
                  {t('automation.runs.downloadReport')}
                </a>
              </>
            ) : (
              <span className="text-ink-subtle">{t('automation.catalog.neverRun')}</span>
            )}
            <span className="text-ink-subtle">·</span>
            <Link
              to="/automation/teams/$teamId/projects/$projectId"
              params={{ teamId: scenario.registryTeamId, projectId: scenario.projectId }}
              search={{ tab: 'classes' }}
              className="text-accent hover:underline"
            >
              {t('automation.catalog.openProject', { name: scenario.projectName })}
            </Link>
            <span className="font-mono text-ink-subtle">{scenario.className}</span>
            {canLink ? (
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto"
                onClick={() => onLink(scenario)}
              >
                <Link2 className="size-3.5" />
                {t('automation.caseLinks.toCase')}
              </Button>
            ) : null}
            {canManage ? (
              <Button
                size="sm"
                variant="ghost"
                className={canLink ? undefined : 'ml-auto'}
                onClick={() => onMove(scenario)}
              >
                <FolderInput className="size-3.5" />
                {t('automation.catalog.move')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
};

/* ─── moving a scenario ─── */

const MoveDialog = ({
  scenario,
  catalog,
  onClose,
}: {
  scenario: AutomationScenarioDto;
  catalog: AutomationCatalogDto;
  onClose: () => void;
}) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const move = useMoveScenario();
  const [areaId, setAreaId] = useState(scenario.areaId);
  const teamName = (id: string | null) => catalog.teams.find((team) => team.id === id)?.name;
  const save = (value: string | null) =>
    move.mutate(
      { projectId: scenario.projectId, className: scenario.className, areaId: value },
      {
        onSuccess: () => {
          toast.success(t('automation.catalog.moved'));
          onClose();
        },
        onError: (error) => toast.error(errorText(error)),
      },
    );
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t('automation.catalog.moveTitle', { name: scenario.title })}
      description={t('automation.catalog.moveHint')}
      footer={
        <>
          {scenario.areaSource === 'manual' ? (
            <Button onClick={() => save(null)} disabled={move.isPending}>
              {t('automation.catalog.moveBack')}
            </Button>
          ) : null}
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={() => save(areaId)} disabled={move.isPending}>
            {t('automation.catalog.moveSave')}
          </Button>
        </>
      }
    >
      <Field label={t('automation.catalog.area')}>
        {({ id }) => (
          <SelectMenu id={id} value={areaId} onValueChange={setAreaId}>
            {catalog.areas.map((area) => (
              <SelectMenuItem key={area.id} value={area.id}>
                {area.teamId ? `${teamName(area.teamId)} / ${area.name}` : area.name}
              </SelectMenuItem>
            ))}
          </SelectMenu>
        )}
      </Field>
    </Dialog>
  );
};

/* ─── the view ─── */

export const CatalogView = () => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const can = useAutomationCan();
  const permissions = usePermissions();
  const catalog = useCatalog();
  const runArea = useRunArea();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [query, setQuery] = useState('');
  const [moving, setMoving] = useState<AutomationScenarioDto | null>(null);
  const [linking, setLinking] = useState<AutomationScenarioDto | null>(null);
  const [confirmRun, setConfirmRun] = useState(false);

  const data = catalog.data;
  const index = useMemo(() => {
    if (!data) return null;
    const areaById = new Map(data.areas.map((a) => [a.id, a]));
    const scenariosIn = (ids: Set<string>) => data.scenarios.filter((s) => ids.has(s.areaId));
    const teamAreaIds = (teamId: string) =>
      new Set(data.areas.filter((a) => a.teamId === teamId).map((a) => a.id));
    return { areaById, scenariosIn, teamAreaIds };
  }, [data]);

  if (catalog.error) {
    return (
      <Card>
        <ErrorState error={catalog.error} onRetry={() => void catalog.refetch()} />
      </Card>
    );
  }
  if (!data || !index) return <SkeletonRows rows={6} className="p-0" />;
  if (data.scenarios.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Folder className="size-6" />}
          title={t('automation.catalog.empty')}
          description={t('automation.catalog.emptyHint')}
        />
      </Card>
    );
  }

  const teamsWithTests = data.teams.filter(
    (team) => index.scenariosIn(index.teamAreaIds(team.id)).length,
  );
  const current: Selection =
    selection ??
    (teamsWithTests[0]
      ? { kind: 'team', id: teamsWithTests[0].id }
      : { kind: 'area', id: UNSORTED });
  const scopeIds =
    current.kind === 'team' ? index.teamAreaIds(current.id) : descendants(data.areas, current.id);
  const needle = query.trim().toLowerCase();
  const searching = needle.length > 0;
  const shown = (searching ? data.scenarios : index.scenariosIn(scopeIds)).filter(
    (s) =>
      !searching ||
      s.title.toLowerCase().includes(needle) ||
      s.className.toLowerCase().includes(needle) ||
      s.caseKeys.some((key) => key.toLowerCase().includes(needle)),
  );
  const totals = countOf(shown);
  const passed = shown.filter((s) => s.last?.status === 'passed').length;
  const neverRun = shown.filter((s) => !s.last).length;
  const repos = new Set(shown.map((s) => s.projectId)).size;

  const areaPath = (s: AutomationScenarioDto) => {
    const area = index.areaById.get(s.areaId);
    const team = data.teams.find((x) => x.id === area?.teamId);
    return area ? (team ? `${team.name} / ${area.name}` : area.name) : null;
  };

  // a team or an area with sub-areas lists its scenarios under area headings
  const groups = (() => {
    if (searching) return [{ id: 'search', title: null as string | null, items: shown }];
    const byArea = new Map<string, AutomationScenarioDto[]>();
    for (const s of shown) byArea.set(s.areaId, [...(byArea.get(s.areaId) ?? []), s]);
    const ordered = data.areas.filter((a) => byArea.has(a.id));
    if (ordered.length === 1)
      return [{ id: ordered[0]!.id, title: null, items: byArea.get(ordered[0]!.id)! }];
    return ordered.map((a) => ({ id: a.id, title: a.name, items: byArea.get(a.id)! }));
  })();

  const selectedTeam =
    current.kind === 'team'
      ? data.teams.find((x) => x.id === current.id)
      : data.teams.find((x) => x.id === index.areaById.get(current.id)?.teamId);
  const heading = searching
    ? t('automation.catalog.searchResults', { query: query.trim() })
    : current.kind === 'team'
      ? (selectedTeam?.fullName ?? '')
      : (index.areaById.get(current.id)?.name ?? '');
  const runTarget = current.kind === 'area' ? current.id : null;

  const isSelected = (sel: Selection) =>
    !searching && current.kind === sel.kind && current.id === sel.id;

  /* the tree: teams → areas with tests (sub-areas under their parent) */
  const tree = (
    <nav aria-label={t('automation.catalog.tree')} className="space-y-0.5">
      {teamsWithTests.map((team) => {
        const teamIds = index.teamAreaIds(team.id);
        const teamOpen = current.kind === 'team' ? current.id === team.id : teamIds.has(current.id);
        const roots = data.areas.filter(
          (a) =>
            a.teamId === team.id &&
            !a.parentId &&
            index.scenariosIn(descendants(data.areas, a.id)).length,
        );
        return (
          <div key={team.id}>
            <TreeRow
              label={team.name}
              title={
                team.qa.length
                  ? t('automation.catalog.qa', { names: team.qa.join(', ') })
                  : team.fullName
              }
              counts={countOf(index.scenariosIn(teamIds))}
              depth={0}
              open={teamOpen}
              selected={isSelected({ kind: 'team', id: team.id })}
              onSelect={() => {
                setQuery('');
                setSelection({ kind: 'team', id: team.id });
              }}
            />
            {teamOpen
              ? roots.map((root) => {
                  const children = data.areas.filter(
                    (a) =>
                      a.parentId === root.id &&
                      index.scenariosIn(descendants(data.areas, a.id)).length,
                  );
                  return (
                    <div key={root.id}>
                      <TreeRow
                        label={root.name}
                        counts={countOf(index.scenariosIn(descendants(data.areas, root.id)))}
                        depth={1}
                        selected={isSelected({ kind: 'area', id: root.id })}
                        onSelect={() => {
                          setQuery('');
                          setSelection({ kind: 'area', id: root.id });
                        }}
                      />
                      {children.map((child) => (
                        <TreeRow
                          key={child.id}
                          label={child.name}
                          counts={countOf(index.scenariosIn(new Set([child.id])))}
                          depth={2}
                          selected={isSelected({ kind: 'area', id: child.id })}
                          onSelect={() => {
                            setQuery('');
                            setSelection({ kind: 'area', id: child.id });
                          }}
                        />
                      ))}
                    </div>
                  );
                })
              : null}
          </div>
        );
      })}
      {[SMOKE, UNSORTED].map((id) => {
        const list = index.scenariosIn(new Set([id]));
        if (!list.length) return null;
        return (
          <TreeRow
            key={id}
            label={index.areaById.get(id)?.name ?? id}
            icon={id === SMOKE ? 'bolt' : 'help'}
            counts={countOf(list)}
            depth={0}
            selected={isSelected({ kind: 'area', id })}
            onSelect={() => {
              setQuery('');
              setSelection({ kind: 'area', id });
            }}
          />
        );
      })}
    </nav>
  );

  return (
    <div className="grid gap-4 md:grid-cols-[minmax(220px,260px)_minmax(0,1fr)]">
      <Card className="h-fit p-2">
        <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
          {t('automation.catalog.tree')}
        </p>
        {tree}
      </Card>

      <section className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1 basis-56">
            {!searching && selectedTeam ? (
              <p className="text-[12px] text-ink-muted">
                {selectedTeam.fullName}
                {selectedTeam.qa.length
                  ? ` · ${t('automation.catalog.qa', { names: selectedTeam.qa.join(', ') })}`
                  : ''}
              </p>
            ) : null}
            <h2 className="text-[16px] font-semibold text-ink">{heading}</h2>
          </div>
          <div className="relative w-full max-w-64 sm:w-64">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-subtle"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('automation.catalog.search')}
              aria-label={t('automation.catalog.search')}
              className="pl-8"
            />
          </div>
          {can.run && runTarget && !searching ? (
            <Button
              variant="primary"
              onClick={() => setConfirmRun(true)}
              disabled={runArea.isPending}
            >
              <Play className="size-3.5" />
              {t('automation.catalog.runFolder')}
            </Button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
          {passed ? (
            <span className={cn('rounded-md px-1.5 py-0.5', STATUS_STYLE.passed)}>
              {t('automation.catalog.passing', { count: passed })}
            </span>
          ) : null}
          {totals.red ? (
            <span className={cn('rounded-md px-1.5 py-0.5', STATUS_STYLE.failed)}>
              {t('automation.catalog.failing', { count: totals.red })}
            </span>
          ) : null}
          {neverRun ? (
            <span className={cn('rounded-md px-1.5 py-0.5', STATUS_STYLE.none)}>
              {t('automation.catalog.notRun', { count: neverRun })}
            </span>
          ) : null}
          <span>
            {t('automation.catalog.scenarios', { count: totals.scenarios })} ·{' '}
            {t('automation.catalog.checks', { count: totals.tests })} ·{' '}
            {t('automation.catalog.repos', { count: repos })}
          </span>
        </div>

        {shown.length === 0 ? (
          <Card>
            <p className="px-4 py-6 text-center text-[13px] text-ink-muted">
              {t('automation.catalog.nothingFound')}
            </p>
          </Card>
        ) : (
          groups.map((group) => (
            <Card key={group.id}>
              {group.title ? (
                <h3 className="border-b border-border px-4 py-2 text-[13px] font-semibold text-ink">
                  {group.title}
                </h3>
              ) : null}
              <ul>
                {[...group.items]
                  .sort(
                    (a, b) =>
                      Number(b.last?.status === 'failed') - Number(a.last?.status === 'failed') ||
                      a.title.localeCompare(b.title, 'ru'),
                  )
                  .map((scenario) => (
                    <ScenarioRow
                      key={`${scenario.projectId}:${scenario.className}`}
                      scenario={scenario}
                      areaPath={searching ? areaPath(scenario) : null}
                      canManage={can.manage}
                      canLink={permissions.can('case:write')}
                      onMove={setMoving}
                      onLink={setLinking}
                    />
                  ))}
              </ul>
            </Card>
          ))
        )}
      </section>

      {moving ? (
        <MoveDialog scenario={moving} catalog={data} onClose={() => setMoving(null)} />
      ) : null}
      {linking ? <LinkToCaseDialog scenario={linking} onClose={() => setLinking(null)} /> : null}
      <ConfirmDialog
        open={confirmRun}
        onOpenChange={setConfirmRun}
        title={t('automation.catalog.runTitle', { name: heading })}
        description={t('automation.catalog.runHint', {
          scenarios: totals.scenarios,
          repos,
        })}
        confirmLabel={t('automation.catalog.runFolder')}
        destructive={false}
        loading={runArea.isPending}
        onConfirm={() =>
          runTarget &&
          runArea.mutate(runTarget, {
            onSuccess: (runs) => {
              setConfirmRun(false);
              const started = runs.filter((r) => r.pipelineId);
              toast.success(
                t('automation.catalog.runStarted', {
                  list: started.map((r) => `${r.projectName} #${r.pipelineId}`).join(', '),
                }),
              );
              for (const failed of runs.filter((r) => !r.pipelineId)) {
                toast.error(`${failed.projectName}: ${failed.error ?? ''}`);
              }
            },
            onError: (error) => {
              setConfirmRun(false);
              toast.error(errorText(error));
            },
          })
        }
      />
    </div>
  );
};
